import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Calendar, ChevronLeft, ChevronRight, Clock, User, Mail, Phone, 
  MessageSquare, CheckCircle, XCircle, Loader2, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-amber-500', textColor: 'text-amber-500' },
  confirmed: { label: 'Confirmé', color: 'bg-blue-500', textColor: 'text-blue-500' },
  completed: { label: 'Terminé', color: 'bg-green-500', textColor: 'text-green-500' },
  cancelled: { label: 'Annulé', color: 'bg-red-500', textColor: 'text-red-500' },
};

const CRMPlanning = () => {
  const { token } = useAuth();
  const { darkMode } = useTheme();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await fetch(`${API_URL}/api/crm/appointments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAppointments(await response.json());
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await fetch(`${API_URL}/api/crm/appointments/${id}/status?status=${status}`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAppointments();
      setSelectedAppointment(null);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce RDV ?')) return;
    try {
      await fetch(`${API_URL}/api/crm/appointments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchAppointments();
      setSelectedAppointment(null);
    } catch (error) {
      console.error('Error deleting appointment:', error);
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay() || 7; // Monday = 1

    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i > 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i + 1),
        isCurrentMonth: false
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const formatDateKey = (date) => {
    return date.toISOString().split('T')[0];
  };

  const getAppointmentsForDate = (date) => {
    const dateKey = formatDateKey(date);
    return appointments.filter(a => a.date === dateKey);
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const days = getDaysInMonth(currentDate);
  const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  const today = formatDateKey(new Date());

  // Upcoming appointments (next 7 days)
  const upcomingAppointments = appointments
    .filter(a => {
      const appointmentDate = new Date(a.date);
      const now = new Date();
      const weekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      return appointmentDate >= now && appointmentDate <= weekLater && a.status !== 'cancelled';
    })
    .sort((a, b) => new Date(a.date + 'T' + a.time) - new Date(b.date + 'T' + b.time));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className={`text-3xl font-inter font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Planning
        </h1>
        <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Gérez vos rendez-vous
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className={`lg:col-span-2 ${darkMode ? 'bg-gray-800 border-gray-700' : ''}`}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className={darkMode ? 'text-white' : ''}>
                {currentDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={prevMonth}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => setCurrentDate(new Date())}>
                  Aujourd'hui
                </Button>
                <Button size="sm" variant="outline" onClick={nextMonth}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Week days header */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {weekDays.map(day => (
                <div key={day} className={`text-center text-sm font-medium py-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const dateKey = formatDateKey(day.date);
                const dayAppointments = getAppointmentsForDate(day.date);
                const isToday = dateKey === today;
                const hasAppointments = dayAppointments.length > 0;

                return (
                  <div
                    key={index}
                    className={`
                      min-h-[80px] p-2 rounded-lg border transition-all cursor-pointer
                      ${day.isCurrentMonth 
                        ? darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                        : darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-100'
                      }
                      ${isToday ? 'ring-2 ring-amber-500' : ''}
                      hover:shadow-md
                    `}
                  >
                    <div className={`text-sm font-medium mb-1 ${
                      !day.isCurrentMonth 
                        ? darkMode ? 'text-gray-600' : 'text-gray-400'
                        : isToday 
                          ? 'text-amber-500' 
                          : darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {day.date.getDate()}
                    </div>
                    {hasAppointments && (
                      <div className="space-y-1">
                        {dayAppointments.slice(0, 2).map(apt => (
                          <div
                            key={apt.id}
                            onClick={() => setSelectedAppointment(apt)}
                            className={`text-xs p-1 rounded truncate ${STATUS_CONFIG[apt.status]?.color} text-white`}
                          >
                            {apt.time} - {apt.firstName}
                          </div>
                        ))}
                        {dayAppointments.length > 2 && (
                          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            +{dayAppointments.length - 2} autres
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Selected Appointment Detail */}
          {selectedAppointment && (
            <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
              <CardHeader>
                <CardTitle className={`text-lg ${darkMode ? 'text-white' : ''}`}>
                  Détails du RDV
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs text-white ${STATUS_CONFIG[selectedAppointment.status]?.color}`}>
                    {STATUS_CONFIG[selectedAppointment.status]?.label}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className={`flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <User className="w-4 h-4" />
                    <span>{selectedAppointment.firstName} {selectedAppointment.lastName}</span>
                  </div>
                  <div className={`flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(selectedAppointment.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                  </div>
                  <div className={`flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Clock className="w-4 h-4" />
                    <span>{selectedAppointment.time} (1h)</span>
                  </div>
                  <div className={`flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{selectedAppointment.email}</span>
                  </div>
                  <div className={`flex items-center gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <Phone className="w-4 h-4" />
                    <span>{selectedAppointment.phone}</span>
                  </div>
                  {selectedAppointment.reason && (
                    <div className={`flex items-start gap-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <MessageSquare className="w-4 h-4 mt-1" />
                      <span className="text-sm">{selectedAppointment.reason}</span>
                    </div>
                  )}
                  {selectedAppointment.comments && (
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {selectedAppointment.comments}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  {selectedAppointment.status === 'pending' && (
                    <Button size="sm" onClick={() => updateStatus(selectedAppointment.id, 'confirmed')} className="bg-blue-500 hover:bg-blue-600">
                      <CheckCircle className="w-4 h-4 mr-1" /> Confirmer
                    </Button>
                  )}
                  {(selectedAppointment.status === 'pending' || selectedAppointment.status === 'confirmed') && (
                    <>
                      <Button size="sm" onClick={() => updateStatus(selectedAppointment.id, 'completed')} className="bg-green-500 hover:bg-green-600">
                        Terminé
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => updateStatus(selectedAppointment.id, 'cancelled')} className="text-red-500 border-red-500">
                        <XCircle className="w-4 h-4 mr-1" /> Annuler
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="outline" onClick={() => deleteAppointment(selectedAppointment.id)} className="text-red-500">
                    Supprimer
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Upcoming Appointments */}
          <Card className={darkMode ? 'bg-gray-800 border-gray-700' : ''}>
            <CardHeader>
              <CardTitle className={`text-lg ${darkMode ? 'text-white' : ''}`}>
                📅 Prochains RDV
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingAppointments.length > 0 ? (
                <div className="space-y-3">
                  {upcomingAppointments.map(apt => (
                    <div
                      key={apt.id}
                      onClick={() => setSelectedAppointment(apt)}
                      className={`p-3 rounded-lg cursor-pointer transition-all ${
                        darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'
                      } ${selectedAppointment?.id === apt.id ? 'ring-2 ring-amber-500' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {apt.firstName} {apt.lastName}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded-full text-white ${STATUS_CONFIG[apt.status]?.color}`}>
                          {STATUS_CONFIG[apt.status]?.label}
                        </span>
                      </div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {new Date(apt.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })} à {apt.time}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Aucun RDV prévu cette semaine</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CRMPlanning;
